import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    ScrollView,
    View
} from "react-native";
import type SubEventCreateRouteProp from "./type";
import type {
    ExtraNotification,
    SubEventType,
    FormItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    DateTimePicker,
    PageContainer,
    TextAreaInput,
    TextInput,
    SelectBox,
    Button,
    Dialog,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../../../../navigation/type";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ArrowLeftIcon,
    PlusIcon,
    SaveIcon,
    Trash2
} from "lucide-react-native";
import RESTServiceContext from "../../../../../services/restAPI/context";

const SubEventCreate = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute<SubEventCreateRouteProp>();

    const params = route.params as unknown as Record<string, unknown>;
    const eventID = params?.eventID as string | undefined;

    const [
        startDate,
        setStartDate
    ] = useState<string>("");

    const [
        endDate,
        setEndDate
    ] = useState<string>("");

    const [
        eventLocation,
        setEventLocation
    ] = useState<string>("");

    const [
        entranceCode,
        setEntranceCode
    ] = useState<string>("");

    const [
        isOnlyMembers,
        setIsOnlyMembers
    ] = useState<boolean>(false);

    const [
        isFinishedEvent,
        setIsFinishedEvent
    ] = useState<boolean>(false);

    const [
        type,
        setType
    ] = useState<SubEventType>("standart");

    const [
        applicationStartDate,
        setApplicationStartDate
    ] = useState<string>("");

    const [
        applicationEndDate,
        setApplicationEndDate
    ] = useState<string>("");

    const [
        formID,
        setFormID
    ] = useState<string>("");

    const [
        startNotificationTitle,
        setStartNotificationTitle
    ] = useState<string>("");

    const [
        startNotificationDescription,
        setStartNotificationDescription
    ] = useState<string>("");

    const [
        finishNotificationDescription,
        setFinishNotificationDescription
    ] = useState<string>("");

    const [
        finishNotificationTitle,
        setFinishNotificationTitle
    ] = useState<string>("");

    const [
        applicationStartNotificationTitle,
        setApplicationStartNotificationTitle
    ] = useState<string>("");

    const [
        applicationStartNotificationDescription,
        setApplicationStartNotificationDescription
    ] = useState<string>("");

    const [
        applicationEndNotificationTitle,
        setApplicationEndNotificationTitle
    ] = useState<string>("");

    const [
        applicationEndNotificationDescription,
        setApplicationEndNotificationDescription
    ] = useState<string>("");

    const [
        formsData,
        setFormsData
    ] = useState<FormItem[]>([]);
    const [
        isThreeTModule,
        setIsThreeTModule
    ] = useState<boolean>(false);

    const [
        extraNotifications,
        setExtraNotifications
    ] = useState<ExtraNotification[]>([]);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isDialogVisible,
        setIsDialogVisible
    ] = useState<boolean>(false);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const formSelectRef = useRef<import("react").ElementRef<typeof SelectBox>>(null);

    useEffect(() => {
        const checkModule = async () => {
            if (eventID) {
                try {
                    const result = await RESTServiceContext.action("getEventForAdmin", {
                        eventID
                    });
                    if (result.payload?.moduleName === "threeT") {
                        setIsThreeTModule(true);
                    }
                } catch {
                    setIsThreeTModule(false);
                }
            }
        };
        checkModule();
    }, [
        eventID
    ]);

    useEffect(() => {
        if (type === "application") {
            const fetchForms = async () => {
                try {
                    const result = await RESTServiceContext.action("getAllFormsForAdmin", null);
                    if (result.payload) {
                        const mappedData = result.payload.map((f: { title: string; id: string; }) => {
                            return {
                                title: f.title,
                                value: f.id
                            };
                        });
                        setFormsData(mappedData);
                        formSelectRef.current?.updateData(mappedData, []);
                    }
                } catch (error) {
                    console.error("Error fetching forms", error);
                }
            };
            fetchForms();
        }
    }, [type]);

    const handleAddNotification = () => {
        setExtraNotifications([
            ...extraNotifications,
            {
                id: Math.random().toString(),
                description: "",
                targetDate: "",
                title: ""
            }
        ]);
    };

    const handleRemoveNotification = (id: string) => {
        setExtraNotifications(extraNotifications.filter(n => n.id !== id));
    };

    const handleUpdateNotification = (id: string, field: keyof ExtraNotification, value: string) => {
        setExtraNotifications(extraNotifications.map(n => {
            if (n.id === id) {
                return {
                    ...n,
                    [field]: value
                };
            }
            return n;
        }));
    };

    const handleSubmit = async () => {
        if (!startDate || !endDate || !eventLocation || !startNotificationTitle || !startNotificationDescription || !finishNotificationTitle || !finishNotificationDescription) {
            NCoreUIKitSnackBar.open({
                title: localize("events-error-save")
            });
            return;
        }

        if (type === "application") {
            if (!applicationStartDate || !applicationEndDate || !formID || !entranceCode || !applicationStartNotificationTitle || !applicationStartNotificationDescription || !applicationEndNotificationTitle || !applicationEndNotificationDescription) {
                NCoreUIKitSnackBar.open({
                    title: localize("events-error-save")
                });
                return;
            }
        }

        setIsDialogVisible(true);
    };

    const handleConfirmSave = async () => {
        setIsDialogVisible(false);
        setIsSubmitting(true);
        try {
            const notifications = extraNotifications.map(n => ({
                targetDate: new Date(n.targetDate).toISOString(),
                description: n.description,
                title: n.title
            }));

            const payload: Record<string, unknown> = {
                startDate: new Date(startDate).toISOString(),
                endDate: new Date(endDate).toISOString(),
                finishNotificationDescription,
                startNotificationDescription,
                finishNotificationTitle,
                startNotificationTitle,
                eventLocation,
                isOnlyMembers,
                notifications,
                eventID,
                type
            };

            if (type === "standart") {
                payload.isFinishedEvent = isFinishedEvent;
            }

            console.log(startDate, endDate);

            if (type === "application") {
                payload.applicationStartDate = new Date(applicationStartDate).toISOString();
                payload.applicationEndDate = new Date(applicationEndDate).toISOString();
                payload.applicationStartNotificationTitle = applicationStartNotificationTitle;
                payload.applicationStartNotificationDescription = applicationStartNotificationDescription;
                payload.applicationEndNotificationTitle = applicationEndNotificationTitle;
                payload.applicationEndNotificationDescription = applicationEndNotificationDescription;
                payload.entranceCode = entranceCode;
                payload.formID = formID;
            }

            const result = await RESTServiceContext.action("createSubEvent", payload as unknown as import("../../../../../services/restAPI/actions/createSubEvent/type").CreateSubEventRequestType);

            const createResult = result as unknown as { error?: boolean, code: number, data?: { id?: string } };
            if (createResult.code === 201) {
                NCoreUIKitSnackBar.open({
                    title: localize("events-success-create")
                });

                navigation.goBack();
            } else {
                NCoreUIKitSnackBar.open({
                    title: localize("events-error-save")
                });
                setIsSubmitting(false);
                return;
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("events-error-save")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    alignItems: "center",
                    flexDirection: "row",
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                icon={() => <ArrowLeftIcon
                    color={colors.project.brandWhite}
                    size={24}
                />}
                customBorderColor="secondaryButton"
                onPress={() => navigation.goBack()}
                customColor="secondaryButton"
                spreadBehaviour="free"
                variant="filled"
            />
            <View
                style={stylesheet.flex1}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleLargeSize"
                >
                    {localize("event-detail-create-new-sub-event")}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    style={{
                        marginTop: spaces.spacingXs
                    }}
                    variant="bodyMediumSize"
                >
                    {localize("events-basic-info-subtitle")}
                </Text>
            </View>
        </View>;
    };

    const renderTimeAndLocation = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={{
                        flexDirection: "column",
                        gap: spaces.spacingMd
                    }}
                >
                    <DateTimePicker
                        onChange={(val) => {
                            if (val.date) {
                                setStartDate(typeof val.date === "string" ? val.date : val.date.toISOString());
                            } else {
                                setStartDate("");
                            }
                        }}
                        initialDate={startDate ? new Date(startDate) : undefined}
                        title={localize("events-start-date")}
                        titleFormat="DD.MM.YYYY HH:mm"
                        placeholder="gg.aa.yyyy ss:dd"
                        isWorkWithRealtime={false}
                        spreadBehaviour="stretch"
                        pickerType="date-time"
                    />
                    <DateTimePicker
                        onChange={(val) => {
                            if (val.date) {
                                setEndDate(typeof val.date === "string" ? val.date : val.date.toISOString());
                            } else {
                                setEndDate("");
                            }
                        }}
                        initialDate={endDate ? new Date(endDate) : undefined}
                        title={localize("events-end-date")}
                        titleFormat="DD.MM.YYYY HH:mm"
                        placeholder="gg.aa.yyyy ss:dd"
                        isWorkWithRealtime={false}
                        spreadBehaviour="stretch"
                        pickerType="date-time"
                    />
                    <TextInput
                        title={localize("events-location")}
                        onChangeText={setEventLocation}
                        spreadBehaviour="stretch"
                        value={eventLocation}
                    />
                    <TextInput
                        title={localize("events-start-notification-title")}
                        onChangeText={setStartNotificationTitle}
                        value={startNotificationTitle}
                        spreadBehaviour="stretch"
                    />
                    <TextAreaInput
                        title={localize("events-start-notification-desc")}
                        onChangeText={setStartNotificationDescription}
                        value={startNotificationDescription}
                        isUpdateOnRealtime={true}
                    />
                    <TextInput
                        title={localize("events-finish-notification-title")}
                        onChangeText={setFinishNotificationTitle}
                        value={finishNotificationTitle}
                        spreadBehaviour="stretch"
                    />
                    <TextAreaInput
                        title={localize("events-finish-notification-desc")}
                        onChangeText={setFinishNotificationDescription}
                        value={finishNotificationDescription}
                        isUpdateOnRealtime={true}
                    />
                </View>
            </View>
        </View>;
    };

    const renderTypeSelection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <View>
                <Text
                    customColor={colors.content.text.high}
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("events-type-section-title")}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    style={{
                        marginTop: spaces.spacingXs
                    }}
                    variant="bodyMediumSize"
                >
                    "Başvurulu" seçilirse, başvuru tarihleri ve form bağlantısı zorunlu olur.
                </Text>
            </View>
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={stylesheet.typeButtonGroup}
                >
                    <Button
                        customBorderColor={type === "standart" ? undefined : "secondaryButton"}
                        customColor={type === "standart" ? undefined : "secondaryButton"}
                        customTextColor={colors.project.brandWhite}
                        title={localize("events-type-standard")}
                        onPress={() => setType("standart")}
                        style={stylesheet.typeButton}
                        spreadBehaviour="free"
                        variant="filled"
                    />
                    <Button
                        customBorderColor={type === "application" ? undefined : "secondaryButton"}
                        customColor={type === "application" ? undefined : "secondaryButton"}
                        customTextColor={colors.project.brandWhite}
                        onPress={() => setType("application")}
                        title={localize("events-type-join")}
                        style={[
                            stylesheet.typeButton,
                            {
                                marginLeft: spaces.spacingMd
                            }
                        ]}
                        spreadBehaviour="free"
                        variant="filled"
                    />
                </View>
                {
                    type === "application" ?
                        <View
                            style={[
                                stylesheet.highlightedContainer,
                                {
                                    marginTop: spaces.spacingLg,
                                    padding: spaces.spacingLg,
                                    borderRadius: radiuses.md,
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.sectionContainer,
                                    {
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <DateTimePicker
                                    onChange={(val) => {
                                        if (val.date) {
                                            setApplicationStartDate(typeof val.date === "string" ? val.date : val.date.toISOString());
                                        } else {
                                            setApplicationStartDate("");
                                        }
                                    }}
                                    initialDate={applicationStartDate ? new Date(applicationStartDate) : undefined}
                                    title={localize("events-app-start-date") + " *"}
                                    contentContainerStyle={{
                                        backgroundColor: colors.project.brandWhite,
                                        borderRadius: radiuses.md
                                    }}
                                    placeholder="gg.aa.yyyy ss:dd"
                                    titleFormat="DD.MM.YYYY HH:mm"
                                    isWorkWithRealtime={false}
                                    spreadBehaviour="stretch"
                                    pickerType="date-time"
                                />
                                <DateTimePicker
                                    onChange={(val) => {
                                        if (val.date) {
                                            setApplicationEndDate(typeof val.date === "string" ? val.date : val.date.toISOString());
                                        } else {
                                            setApplicationEndDate("");
                                        }
                                    }}
                                    initialDate={applicationEndDate ? new Date(applicationEndDate) : undefined}
                                    contentContainerStyle={{
                                        backgroundColor: colors.project.brandWhite,
                                        borderRadius: radiuses.md
                                    }}
                                    title={localize("events-app-end-date") + " *"}
                                    placeholder="gg.aa.yyyy ss:dd"
                                    titleFormat="DD.MM.YYYY HH:mm"
                                    isWorkWithRealtime={false}
                                    spreadBehaviour="stretch"
                                    pickerType="date-time"
                                />
                            </View>
                            <View
                                style={{
                                    marginTop: spaces.spacingMd,
                                    gap: spaces.spacingMd
                                }}
                            >
                                <TextInput
                                    title={localize("events-app-start-notification-title")}
                                    onChangeText={setApplicationStartNotificationTitle}
                                    value={applicationStartNotificationTitle}
                                    customColor={colors.project.brandWhite}
                                    spreadBehaviour="stretch"
                                />
                                <TextAreaInput
                                    onChangeText={setApplicationStartNotificationDescription}
                                    title={localize("events-app-start-notification-desc")}
                                    value={applicationStartNotificationDescription}
                                    customColor={colors.project.brandWhite}
                                    isUpdateOnRealtime={true}
                                />
                                <TextInput
                                    title={localize("events-app-end-notification-title")}
                                    onChangeText={setApplicationEndNotificationTitle}
                                    customColor={colors.project.brandWhite}
                                    value={applicationEndNotificationTitle}
                                    spreadBehaviour="stretch"
                                />
                                <TextAreaInput
                                    onChangeText={setApplicationEndNotificationDescription}
                                    title={localize("events-app-end-notification-desc")}
                                    value={applicationEndNotificationDescription}
                                    customColor={colors.project.brandWhite}
                                    isUpdateOnRealtime={true}
                                />
                            </View>
                            {
                                isThreeTModule ?
                                    <View
                                        style={{
                                            backgroundColor: colors.content.container.danger,
                                            marginBottom: spaces.spacingXs,
                                            marginTop: spaces.spacingXl,
                                            borderRadius: radiuses.md,
                                            padding: spaces.spacingMd
                                        }}
                                    >
                                        <Text
                                            customColor={colors.content.text.danger}
                                            variant="bodyMediumSize"
                                        >
                                            * Sektör eşleştirme öncelikli 3T kullanılacaksa, seçeceğiniz formda 'customer_target_sectors' ve 'suplier_target_sectors' alanlarının bulunduğundan emin olun.
                                        </Text>
                                    </View>
                                :
                                    <View
                                        style={{
                                            marginTop: spaces.spacingXl
                                        }}
                                    />
                            }
                            <SelectBox
                                onChange={(selected) => {
                                    if (selected.length > 0 && selected[0]) {
                                        const item = selected[0] as unknown as { __key?: string, value?: string };
                                        setFormID(item.__key || item.value || "");
                                    } else {
                                        setFormID("");
                                    }
                                }}
                                titleExtractor={(i: unknown) => (i as { title: string }).title}
                                keyExtractor={(i: unknown) => (i as { value: string }).value}
                                spreadBehaviour="stretch"
                                title="Başvuru Formu"
                                ref={formSelectRef}
                                data={formsData}
                            />
                        </View>
                    :
                        null
                }
            </View>
        </View>;
    };

    const renderAccessSettings = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <Text
                customColor={colors.content.text.high}
                style={stylesheet.sectionTitle}
                variant="titleMediumSize"
            >
                {localize("events-members-only-title")}
            </Text>
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={stylesheet.switchRow}
                >
                    <View
                        style={stylesheet.switchTextContainer}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            variant="bodyMediumSize"
                        >
                            {localize("events-members-only-subtitle")}
                        </Text>
                    </View>
                    <Switch
                        onPress={() => setIsOnlyMembers(!isOnlyMembers)}
                        customTheme={{
                            themeKey: "events_form" as never
                        }}
                        isActive={isOnlyMembers}
                        type="neutral"
                    />
                </View>
            </View>
        </View>;
    };

    const renderFinishedEventSetting = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <Text
                customColor={colors.content.text.high}
                style={stylesheet.sectionTitle}
                variant="titleMediumSize"
            >
                {localize("events-sub-is-finished-title")}
            </Text>
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={stylesheet.switchRow}
                >
                    <View
                        style={stylesheet.switchTextContainer}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            variant="bodyMediumSize"
                        >
                            {localize("events-sub-is-finished-desc")}
                        </Text>
                    </View>
                    <Switch
                        onPress={() => setIsFinishedEvent(!isFinishedEvent)}
                        customTheme={{
                            themeKey: "events_form" as never
                        }}
                        isActive={isFinishedEvent}
                        type="neutral"
                    />
                </View>
            </View>
        </View>;
    };

    const renderDynamicNotifications = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <View>
                <Text
                    customColor={colors.content.text.high}
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    Bildirimler
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    style={{
                        marginTop: spaces.spacingXs
                    }}
                    variant="bodyMediumSize"
                >
                    Belirlenen zamanda otomatik olarak gönderilecek bildirimler. İstediğiniz kadar ekleyebilirsiniz.
                </Text>
            </View>
            {extraNotifications.map((notif) => <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
                key={notif.id}
            >
                <View
                    style={{
                        flexDirection: "column",
                        gap: spaces.spacingMd
                    }}
                >
                    <View
                        style={stylesheet.switchRow}
                    >
                        <View
                            style={[
                                stylesheet.notificationBadgeContainer,
                                {
                                    backgroundColor: colors.content.container.warning,
                                    paddingHorizontal: spaces.spacingMd,
                                    paddingVertical: spaces.spacingXs,
                                    borderRadius: radiuses.full
                                }
                            ]}
                        >
                            <Text
                                customColor="project.gold"
                                variant="labelMediumSize"
                            >
                                Bildirim
                            </Text>
                        </View>
                        <Button
                            onPress={() => handleRemoveNotification(notif.id)}
                            icon={({
                                color,
                                size
                            }) => <Trash2
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            spreadBehaviour="free"
                            type="danger"
                        />
                    </View>
                    <TextInput
                        onChangeText={(val) => handleUpdateNotification(notif.id, "title", val)}
                        placeholder={localize("events-notification-title-placeholder")}
                        title={localize("events-notification-title")}
                        spreadBehaviour="stretch"
                        value={notif.title}
                    />
                    <TextAreaInput
                        onChangeText={(val) => handleUpdateNotification(notif.id, "description", val)}
                        placeholder={localize("events-notification-desc-placeholder")}
                        title={localize("events-notification-desc")}
                        value={notif.description}
                        isUpdateOnRealtime={true}
                    />
                    <DateTimePicker
                        onChange={(val) => {
                            if (val.date) {
                                handleUpdateNotification(notif.id, "targetDate", typeof val.date === "string" ? val.date : val.date.toISOString());
                            } else {
                                handleUpdateNotification(notif.id, "targetDate", "");
                            }
                        }}
                        initialDate={notif.targetDate ? new Date(notif.targetDate) : undefined}
                        title={localize("events-notification-date")}
                        placeholder="gg.aa.yyyy ss:dd"
                        titleFormat="DD.MM.YYYY HH:mm"
                        isWorkWithRealtime={false}
                        spreadBehaviour="stretch"
                        pickerType="date-time"
                    />
                </View>
            </View>)}
            <Button
                title={localize("events-add-new-notification")}
                icon={({
                    size
                }) => <PlusIcon
                    color={colors.project.brandWhite}
                    size={size}
                />}
                customBorderColor="secondaryButton"
                onPress={handleAddNotification}
                style={{
                    marginTop: spaces.spacingMd
                }}
                customColor="secondaryButton"
                spreadBehaviour="stretch"
                variant="filled"
            />
        </View>;
    };

    const renderEntranceAndAttendance = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <View>
                <Text
                    customColor={colors.content.text.high}
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    Giriş / Yoklama
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    style={{
                        marginTop: spaces.spacingXs
                    }}
                    variant="bodyMediumSize"
                >
                    Salon girişinde okutulacak giriş kodu.
                </Text>
            </View>
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={stylesheet.qrCodeRow}
                >
                    <View
                        style={stylesheet.qrCodeInput}
                    >
                        <TextInput
                            title={localize("events-entrance-code") + " *"}
                            onChangeText={setEntranceCode}
                            spreadBehaviour="stretch"
                            value={entranceCode}
                        />
                    </View>
                    <Button
                        onPress={() => {
                            const newCode = "GM-" + Math.random().toString(36).substring(2, 6).toUpperCase();
                            setEntranceCode(newCode);
                        }}
                        customBorderColor="secondaryButton"
                        title={localize("events-refresh")}
                        style={{
                            marginLeft: spaces.spacingMd
                        }}
                        customColor="secondaryButton"
                        spreadBehaviour="free"
                        variant="filled"
                    />
                </View>
            </View>
        </View>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <ScrollView
            showsVerticalScrollIndicator={false}
        >
            <View
                style={[
                    stylesheet.container,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                {renderHeader()}
                {renderTimeAndLocation()}
                {type === "application" ? renderEntranceAndAttendance() : null}
                {renderTypeSelection()}
                {renderAccessSettings()}
                {type === "standart" ? renderFinishedEventSetting() : null}
                {renderDynamicNotifications()}
                <View
                    style={[
                        stylesheet.footerActionContainer,
                        {
                            marginTop: spaces.spacingXl
                        }
                    ]}
                >
                    <Button
                        title={localize("events-cancel-button")}
                        customBorderColor="secondaryButton"
                        onPress={() => navigation.goBack()}
                        customColor="secondaryButton"
                        spreadBehaviour="free"
                        variant="filled"
                    />
                    <Button
                        icon={({
                            color,
                            size
                        }) => <SaveIcon
                            color={colors.project.brandWhite || colors.content.icon[color]}
                            size={size}
                        />}
                        customTextColor={colors.project.brandWhite}
                        title={localize("events-create")}
                        style={{
                            marginLeft: spaces.spacingMd
                        }}
                        isLoading={isSubmitting}
                        onPress={handleSubmit}
                        spreadBehaviour="free"
                        variant="filled"
                    />
                </View>
            </View>
        </ScrollView>
        <Dialog
            primaryButtonProps={{
                title: localize("events-create-confirm-yes"),
                onPress: handleConfirmSave
            }}
            secondaryButtonProps={{
                title: localize("events-create-confirm-no"),
                onPress: () => setIsDialogVisible(false),
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onOverlayPress={() => setIsDialogVisible(false)}
            onClosed={() => setIsDialogVisible(false)}
            isVisible={isDialogVisible}
            variant="yes-no"
        >
            <View
                style={{
                    padding: spaces.spacingLg
                }}
            >
                <Text
                    variant="bodyMediumSize"
                >
                    {localize("events-sub-create-confirm-desc")}
                </Text>
            </View>
        </Dialog>
    </PageContainer>;
};

export default SubEventCreate;
